import { FormArray, FormControl, FormGroup } from '@angular/forms';
import {
  FeederContractModel,
  FeederContractReferenceModel
} from '@bo-client/activation-management/feeder-contract/model/feeder-contract.model';
import {
  ConsumerFormV2,
  ConsumerModel
} from '@bo-client/activation-management/grid-registration/model/consumer.model';
import {
  ExistingFeederForm,
  ExistingFeederFormV2,
  ExistingFeederModel
} from '@bo-client/activation-management/grid-registration/model/existing-feeder.model';
import {
  FeederConnectionStandard,
  FeedInCompensationType,
  FeedingPlantTypeEnum,
  FeedInManagementInfoEnum,
  FeedInManagementOrderStateEnum,
  GridRegistrationStatus,
  GridRegistrationTypeEnum,
  NaProtectionTypeEnum,
  PowerGenerationSourceEnum,
  ReactivePowerProvisioningMethod,
  SymmetryConditionsEnum,
  VoltageLevel
} from '@bo-client/activation-management/grid-registration/model/grid-registration.enum';
import {
  ProducerForm,
  ProducerModel
} from '@bo-client/activation-management/grid-registration/model/producer.model';
import {
  PVModuleForm,
  PVModuleModel
} from '@bo-client/activation-management/grid-registration/model/pv-module.model';
import {
  StorageForm,
  StorageModel
} from '@bo-client/activation-management/grid-registration/model/storage.model';
import { CommonStatusEnum } from '@bo-client/activation-management/shared/models/commissioningModel';
import {
  ConnectionObjectAdditionalInfoForm,
  ConnectionObjectAdditionalInfoModel
} from '@bo-client/activation-management/shared/models/connection-object-additional-info.model';
import { MeteringConceptModel } from '@bo-client/activation-management/shared/models/meteringConceptModel';
import { TypedForm } from '@bo-client/shared/forms/generic-forms/typedForm.type';
import { ActivityDto } from '@bo-client/shared/models/activityDto';
import { CompanyLocationDto } from '@bo-client/shared/models/companyLocations';
import { ConnectionObject, ConnectionObjectFormV2 } from '@bo-client/shared/models/connectionObject';
import { ContactDataDto, ContactDataForm } from '@bo-client/shared/models/contactDataDto';
import { DocumentDtoModel, UploadForm } from '@bo-client/shared/models/documentDto';
import { RequestModel } from '@bo-client/shared/models/request.model';
import { UserDto } from '@bo-client/shared/models/userDto';

export class GridRegistrationModel extends RequestModel {
  df: DecimalFormat;
  formVersion: 'v1' | 'v2';
  gridRegistrationType: GridRegistrationTypeEnum;
  previousUsers: UserDto[] = [];
  technician: UserDto;
  companyLocation: CompanyLocationDto;
  customer: ContactDataDto = {};

  invoiceContact: ContactDataDto = {};
  connectionObject: ConnectionObject = {};
  connectionObjectAdditionalInfo: ConnectionObjectAdditionalInfoModel =
    new ConnectionObjectAdditionalInfoModel();
  uniqueCoId: string;
  state: GridRegistrationStatus | CommonStatusEnum;
  uploads: DocumentDtoModel[] = [];
  consumers: ConsumerModel[] = [];
  meteringConcept: MeteringConceptModel = new MeteringConceptModel();
  oldMeteringConcept: MeteringConceptModel | null = null;
  commissioningDate: Date | string | null;
  maLo?: string | null;
  meLo?: string | null;
  trId?: string | null;
  srId?: string | null;
  meterOne: MeterModel | null = null;
  meterTwo: MeterModel | null = null;
  meterThree: MeterModel | null = null;
  deviceAlreadyRequested: boolean | null;
  powerGenerationSource: PowerGenerationSourceEnum;
  isEEGExtention: boolean;
  feedingPlantType: FeedingPlantTypeEnum;
  producers: ProducerModel[] = [];
  storages: StorageModel[] = [];
  pvModules: PVModuleModel[] = [];
  existingFeeders: ExistingFeederModel[] = [];
  contractedConnectionPower: number | null;
  connectionFuseAmplitude: number | null;
  comment: string | null;
  shortDescription?: string;
  longDescription?: string;
  activities: ActivityDto[];
  hasDifferentInvoiceContact: boolean | null;
  hasInstalledDevices: boolean | null;
  hasStorage = false;
  hasInverterInstalled: boolean;
  storageConcept: MeteringConceptModel = new MeteringConceptModel();
  consumptionOver30kw: boolean;
  feedInManagementInfo: FeedInManagementInfoEnum;
  acceptedConditions1: boolean | null;
  acceptedConditions2: boolean | null;
  acceptedConditions3: boolean | null;
  feederContractRefId?: number | null;
  placeInService?: PlaceInServiceModel;
  feederContract?: FeederContractReferenceModel | FeederContractModel;
  maStRNumberProducer?: string | null;
  maStRNumberStorage?: string | null;
  maStRNumber?: string | null;
  maStRSELNumber?: string | null;
  maStREEGNumber?: string | null;
  // Todo: Types überprüfen -> kommt bis jetzt nur null zurück
  usageType?: any | null;
  orientation?: any | null;
  tilt?: any | null;
  secondaryOrientation?: any | null;
  secondaryTilt?: any | null;
  //
  maStRRegistrationDate?: Date | string | null;
  gridConnectionPoint?: string;
  gridConnectionPointId?: string;
  extractionNetworkPowerAuthorized?: number | null;
  feedInManagementSerialNumber?: string | null;
  customerOrderedFeedInManagement?: FeedInManagementOrderStateEnum;
  customerOrderedDate?: Date | string | null;
  meterNumber?: string;
  cosPhi?: string;
  reactivePowerProvisioningMethod?: ReactivePowerProvisioningMethod;
  voltageLevel?: VoltageLevel;
  assignedTransformerStation?: string;
  validityExpirationDate?: string | number | Date;
  existingConsumption?: number | null;
  existingProduction?: number | null;
  installedPower?: number | null;
  newConsumption?: number | null;
  newProduction?: number | null;
  newInstalledPower?: number | null;
  feederConnectionStandard?: FeederConnectionStandard;
  directMarketing?: boolean | null;
  directMarketer?: string | null;
  feedInCompensationType?: FeedInCompensationType | null;
  totalPowerProducers?: string | null;
  totalConsumerPower?: string | null;
  totalPowerPVModules?: string | null;
  token?: string;
  externalId?: string;
  externalCustomerId?: string;
  approvalComment?: string;
  renewalCount?: number;
  fullFeedInBonusRequested: boolean;
  disableMastrValidation?: boolean;
}

export class GridRegistrationReferenceModel {
  id: number;
}

export class PlaceInServiceModel {
  effectiveActivePowerMax?: number;
  effectiveApparentPowerMax?: number;
  effectiveModulePowerWatt?: number;
  effectiveAndRequestedValuesMatch?: boolean;
  invoiceMeteringCheckDone?: boolean;
  vdeCertificateExists?: boolean;
  powerDirectionSensorCheckDone?: boolean;
  vdeArN4100_55_CertificateExists?: boolean;
  powerProtectionCertificateExists?: boolean;
  factorCosphiCheck?: boolean;
  naProtection?: NaProtectionTypeEnum;
  overvoltageProtectionType?: number;
  overvoltageReleaseTestDone?: boolean;
  triggerCircuitTestDone?: boolean;
  pMonitoringExists?: boolean;
  paveTestDone?: boolean;
  effectivePave?: number;
  eegConfirmation?: boolean;
  flawlessFunctionGuarantee?: boolean;
  symmetryConditions?: SymmetryConditionsEnum;
  addedGenerationL1?: number;
  existingGenerationL1?: number;
  addedGenerationL2?: number;
  existingGenerationL2?: number;
  addedGenerationL3?: number;
  existingGenerationL3?: number;
  placeInServiceDatum?: Date;
  vdeConditions?: boolean;
  customerAuthorization? = false;
}

export type PlaceInServiceModelForm = TypedForm<PlaceInServiceModel>;

export class ConfirmGRFeedinOrderModel {
  acceptedConditions: boolean;
}

type DecimalFormat = {
  groupingUsed: boolean;
  parseIntegerOnly: boolean;
  maximumIntegerDigits: number;
  minimumIntegerDigits: number;
  maximumFractionDigits: number;
  minimumFractionDigits: number;
  positivePrefix: string;
  positiveSuffix: string;
  negativePrefix: string;
  negativeSuffix: string;
  multiplier: number;
  groupingSize: number;
  decimalSeparatorAlwaysShown: boolean;
  parseBigDecimal: boolean;
  roundingMode: string;
  currency: string;
  decimalFormatSymbols: DecimalFormatSymbols;
};

type DecimalFormatSymbols = {
  zeroDigit: string;
  groupingSeparator: string;
  decimalSeparator: string;
  perMill: string;
  percent: string;
  digit: string;
  patternSeparator: string;
  infinity: string;
  minusSign: string;
  currencySymbol: string;
  monetaryGroupingSeparator: string;
  currency: string;
  internationalCurrencySymbol: string;
  naN: string;
  monetaryDecimalSeparator: string;
  exponentSeparator: string;
};

type MeterModel = {
  description: string;
  meterNo: string;
  obisCode: string;
  value: string;
};

export type MeterForm = TypedForm<Omit<MeterModel, 'description'>>;

// Allgemeine Form-Komponente
type BaseForm = TypedForm<
  Pick<
    GridRegistrationModel,
    // Attribute, die im Form existieren sollen → Hier weitere Attribute hinzufügen, wenn die Formulare erweitert werden
    | 'acceptedConditions1'
    | 'acceptedConditions2'
    | 'assignedTransformerStation'
    | 'comment'
    | 'connectionFuseAmplitude'
    | 'contractedConnectionPower'
    | 'directMarketer'
    | 'directMarketing'
    | 'existingConsumption'
    | 'existingProduction'
    | 'externalCustomerId'
    | 'externalId'
    | 'feedInCompensationType'
    | 'feedingPlantType'
    | 'feedInManagementInfo'
    | 'feedInManagementSerialNumber'
    | 'formVersion'
    | 'gridConnectionPointId'
    | 'hasDifferentInvoiceContact'
    | 'hasInstalledDevices'
    | 'hasInverterInstalled'
    | 'hasStorage'
    | 'installedPower'
    | 'installerFeedbackComment'
    | 'isEEGExtention'
    | 'longDescription'
    | 'maLo'
    | 'meLo'
    | 'meterNumber'
    | 'newConsumption'
    | 'newProduction'
    | 'newInstalledPower'
    | 'powerGenerationSource'
    | 'shortDescription'
    | 'srId'
    | 'trId'
    // Attribute, die zwar existieren, aber das FormControl anders als der Typ aussieht
    // | 'companyLocation'
    // | 'customer'
    // | 'invoiceContact'
    // | 'connectionObject'
    // | 'connectionObjectAdditionalInfo'
    // | 'state'
    // | 'uploads'
    // | 'consumers'
    // | 'meteringConcept'
    // | 'oldMeteringConcept'
    // | 'storages'
    // | 'existingFeeders'
    // | 'producers'
    // | 'pvModules'
  >
>;

type ChangeMeteringConceptFormBase = TypedForm<
  Pick<
    GridRegistrationModel,
    | 'acceptedConditions1'
    | 'assignedTransformerStation'
    | 'comment'
    | 'externalId'
    | 'installerFeedbackComment'
    | 'formVersion'
    | 'gridConnectionPointId'
    | 'hasDifferentInvoiceContact'
    | 'longDescription'
    | 'meterNumber'
    | 'shortDescription'
    | 'newInstalledPower'
    // Todo: Hier müssen noch approvalComment und externalCustomerId rein wenn die branches gemerged wurden
  >
>;

type ExtendedChangeMeteringConceptForm = FormGroup<{
  commissioningDate: FormControl<string | Date>;
  companyLocation: TypedForm<{ id: number }>;
  customer: ContactDataForm;
  invoiceContact: ContactDataForm;
  connectionObject: ConnectionObjectFormV2;
  connectionObjectAdditionalInfo: ConnectionObjectAdditionalInfoForm;
  uploads: FormArray<UploadForm>;
  meteringConcept: TypedForm<{ id: number }>;
  oldMeteringConcept: TypedForm<{ id: number }>;
  meterOne: MeterForm;
  meterTwo: MeterForm;
  meterThree: MeterForm;
  state: FormControl<(GridRegistrationStatus | CommonStatusEnum) | ''>;
  //Transiente felder
  deviceType: FormControl<number>;
}>;

export type ChangeMeteringConceptForm = FormGroup<
  ChangeMeteringConceptFormBase['controls'] & ExtendedChangeMeteringConceptForm['controls']
>;

// Angepasste Form-Controls
type ExtendedForm = FormGroup<
  {
    companyLocation: TypedForm<{ id: number }>;
    customer: ContactDataForm;
    invoiceContact: ContactDataForm;
    connectionObject: ConnectionObjectFormV2;
    connectionObjectAdditionalInfo: ConnectionObjectAdditionalInfoForm;
    state: FormControl<(GridRegistrationStatus | CommonStatusEnum) | ''>;
    uploads: FormArray<UploadForm>;
    consumers: FormArray<ConsumerFormV2>;
    meteringConcept: TypedForm<{ id: number }>;
    storages: FormArray<StorageForm>;
    existingFeeders: FormArray<ExistingFeederForm>;
    producers: FormArray<ProducerForm>;
    pvModules: FormArray<PVModuleForm>;
    validityExpirationDate: FormControl<GridRegistrationModel['validityExpirationDate']>;
  } & { differingInvoiceContact: FormControl<boolean> }
>;

export type GridRegistrationForm = FormGroup<BaseForm['controls'] & ExtendedForm['controls']>;

export type GridRegistrationFeederForm = FormGroup<
  GridRegistrationForm['controls'] & { fullFeedInBonusRequested: FormControl<boolean> }
>;

/** Consumer grid-registration form (Charger, EHeating, Misc, legacy Storage). */
export type GridRegistrationConsumerForm = FormGroup<
  TypedForm<
    Pick<
      GridRegistrationModel,
      | 'state'
      | 'formVersion'
      | 'comment'
      | 'shortDescription'
      | 'longDescription'
      | 'installerFeedbackComment'
      | 'gridConnectionPointId'
      | 'externalId'
      | 'assignedTransformerStation'
      | 'trId'
      | 'srId'
      | 'meLo'
      | 'maLo'
      | 'extractionNetworkPowerAuthorized'
      | 'acceptedConditions1'
      | 'contractedConnectionPower'
      | 'connectionFuseAmplitude'
      | 'hasInstalledDevices'
    >
  >['controls'] & {
    customer: ContactDataForm;
    invoiceContact: ContactDataForm;
    differingInvoiceContact: FormControl<boolean>;
    actionType: FormControl<number | null>;
    connectionObject: ConnectionObjectFormV2;
    connectionObjectAdditionalInfo: ConnectionObjectAdditionalInfoForm;
    companyLocation: TypedForm<{ id: number }>;
    consumers: FormArray<ConsumerFormV2>;
    meteringConcept: TypedForm<{ id: number }>;
    uploads: FormArray<UploadForm>;
    existingFeeders: FormArray<ExistingFeederForm>;
  }
>;

/** Scalar controls for grid-registration-form-storage-v2 (subset of {@link BaseForm}). */
type GridRegistrationStorageV2FormBase = TypedForm<
  Pick<
    GridRegistrationModel,
    | 'acceptedConditions1'
    | 'acceptedConditions2'
    | 'assignedTransformerStation'
    | 'comment'
    | 'connectionFuseAmplitude'
    | 'contractedConnectionPower'
    | 'directMarketer'
    | 'directMarketing'
    | 'externalCustomerId'
    | 'externalId'
    | 'feedInCompensationType'
    | 'formVersion'
    | 'gridConnectionPointId'
    | 'hasDifferentInvoiceContact'
    | 'hasInstalledDevices'
    | 'hasInverterInstalled'
    | 'hasStorage'
    | 'installerFeedbackComment'
    | 'longDescription'
    | 'maLo'
    | 'meLo'
    | 'meterNumber'
    | 'shortDescription'
    | 'srId'
    | 'trId'
  >
>;

export type GridRegistrationStorageV2Form = FormGroup<
  GridRegistrationStorageV2FormBase['controls'] &
    Omit<ExtendedForm['controls'], 'existingFeeders'> & {
      existingFeeders: FormArray<ExistingFeederFormV2>;
    } & { fullFeedInBonusRequested: FormControl<boolean> }
>;

export type PlaceInServiceRequestForm = FormGroup<{
  deviceType: FormControl<number>;
  placeInService: PlaceInServiceModelForm;
  newProduction: FormControl<number>;
  newInstalledPower: FormControl<number>;
  pvModules: FormArray<PVModuleForm>;
  producers: FormArray<ProducerForm>;
  storages: FormArray<StorageForm>;
  consumers: FormArray<ConsumerFormV2>;
  uploads: FormArray<UploadForm>;
  meterNumber: FormControl<string>;
  meterOne: MeterForm;
  meterTwo: MeterForm;
  meterThree: MeterForm;
  meteringConcept: TypedForm<{ id: number }>;
  /*feedInManagementSerialNumber: FormControl<string>;*/
  maStRNumberProducer: FormControl<string>;
  maStRNumberStorage: FormControl<string>;
  maStRSSENumberStorage: FormControl<string>;
  maStREEGNumber: FormControl<string>;
  maStRNumber: FormControl<string>;
  maStRAcceptedDeclaration: FormControl<boolean>;
  maStRRegistrationDate: FormControl;
  customer: ContactDataForm;
}>;
